import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiCreditCard, FiEye, FiEyeOff, FiCheck, FiDollarSign } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

interface GatewayField {
  label: string;
  label_ar: string;
  type: string;
}

interface Gateway {
  gateway: string;
  name: string;
  name_ar: string;
  fields: Record<string, GatewayField>;
  is_enabled: boolean;
  test_mode: boolean;
  test_credentials: Record<string, string>;
  live_credentials: Record<string, string>;
  has_test_credentials: boolean;
  has_live_credentials: boolean;
  /** Settled through Hadaq's MyFatoorah account — the seller supplies no keys. */
  managed?: boolean;
  hadaq_only?: boolean;
  supplier_status?: 'none' | 'pending' | 'active' | 'failed' | null;
  supplier_code?: number | null;
  supplier_error?: string | null;
}

export default function PaymentGateways() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [testForm, setTestForm] = useState<Record<string, string>>({});
  const [liveForm, setLiveForm] = useState<Record<string, string>>({});
  const [testMode, setTestMode] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [credTab, setCredTab] = useState<'test' | 'live'>('test');

  useEffect(() => {
    if (store?.id) loadGateways();
  }, [store?.id]);

  const loadGateways = async () => {
    setLoading(true);
    try { const res = await api.get(`/stores/${store.id}/payment-gateways`); setGateways(res.data); }
    finally { setLoading(false); }
  };

  const openEdit = (gw: Gateway) => {
    setEditing(gw.gateway);
    setTestMode(gw.test_mode);
    setCredTab(gw.test_mode ? 'test' : 'live');
    const testInit: Record<string, string> = {};
    const liveInit: Record<string, string> = {};
    Object.keys(gw.fields).forEach((key) => {
      testInit[key] = gw.test_credentials?.[key] || '';
      liveInit[key] = gw.live_credentials?.[key] || '';
    });
    setTestForm(testInit);
    setLiveForm(liveInit);
    setShowSecrets({});
  };

  const handleSave = async (gwKey: string) => {
    setSaving(true);
    try {
      await api.put(`/stores/${store.id}/payment-gateways/${gwKey}`, {
        test_credentials: testForm,
        live_credentials: liveForm,
        test_mode: testMode,
      });
      toast.success(dt('settings_saved', lang));
      setEditing(null);
      loadGateways();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving', lang));
    }
    setSaving(false);
  };

  const handleToggle = async (gw: Gateway) => {
    // A managed gateway has no keys to enter: turning it on registers the
    // store with MyFatoorah, which is what can fail.
    if (!gw.managed) {
      const hasCreds = testMode ? gw.has_test_credentials : gw.has_live_credentials;
      if (!hasCreds && !gw.is_enabled) {
        toast.error(lang === 'ar' ? 'أضف مفاتيح API أولاً' : 'Add API keys first');
        openEdit(gw);
        return;
      }
    }
    try {
      const res = await api.put(`/stores/${store.id}/payment-gateways/${gw.gateway}`, {
        is_enabled: !gw.is_enabled,
      });
      setGateways((prev) =>
        prev.map((g) => g.gateway === gw.gateway
          ? { ...g, is_enabled: !g.is_enabled, supplier_status: res.data?.supplier_status ?? g.supplier_status, supplier_error: null }
          : g)
      );
      toast.success(gw.is_enabled
        ? (lang === 'ar' ? 'تم تعطيل البوابة' : 'Gateway disabled')
        : (lang === 'ar' ? 'تم تفعيل البوابة' : 'Gateway enabled')
      );
    } catch (err: any) {
      const message = err.response?.data?.message;
      toast.error(message || dt('error_saving', lang));
      if (message && gw.managed) {
        setGateways((prev) =>
          prev.map((g) => g.gateway === gw.gateway ? { ...g, supplier_status: 'failed', supplier_error: message } : g)
        );
      }
    }
  };

  const handleModeToggle = async (gw: Gateway) => {
    const newMode = !gw.test_mode;
    try {
      await api.put(`/stores/${store.id}/payment-gateways/${gw.gateway}`, {
        test_mode: newMode,
      });
      setGateways((prev) =>
        prev.map((g) => g.gateway === gw.gateway ? { ...g, test_mode: newMode } : g)
      );
      toast.success(newMode
        ? (lang === 'ar' ? 'تم التبديل إلى الوضع التجريبي' : 'Switched to Test mode')
        : (lang === 'ar' ? 'تم التبديل إلى الوضع الحقيقي' : 'Switched to Live mode')
      );
    } catch {
      toast.error(dt('error_saving', lang));
    }
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  const renderCredFields = (fields: Record<string, GatewayField>, form: Record<string, string>, setForm: (f: Record<string, string>) => void, hasCreds: boolean, prefix: string) => (
    <div>
      {Object.entries(fields).map(([key, field]) => (
        <div className="form-group" key={key} style={{ marginBottom: 12 }}>
          <label className="form-label" style={{ fontSize: 13 }}>
            {lang === 'ar' ? field.label_ar : field.label}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={field.type === 'password' && !showSecrets[prefix + key] ? 'password' : 'text'}
              className="form-input"
              value={form[key] || ''}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              placeholder={hasCreds
                ? t('Leave empty to keep current', 'اترك فارغاً للاحتفاظ بالقيمة الحالية')
                : ''}
              dir="ltr"
              style={{ paddingInlineEnd: field.type === 'password' ? 40 : undefined }}
            />
            {field.type === 'password' && (
              <button
                type="button"
                onClick={() => toggleShowSecret(prefix + key)}
                style={{
                  position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4, zIndex: 1,
                }}
              >
                {showSecrets[prefix + key] ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1>{dt('payment_gateways', lang)}</h1>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {gateways.map((gw) => (
          <div key={gw.gateway} className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editing === gw.gateway ? 20 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {(gw.gateway === 'cbk_knet') ? (
                  <img src="/images/cbk-logo.jpg" alt="CBK" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', border: '1px solid var(--gray-200)' }} />
                ) : (gw.gateway === 'tahseeel_knet' || gw.gateway === 'tahseeel_cc') ? (
                  <img src="/images/tahseeel-logo.png" alt="Tahseeel" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', border: '1px solid var(--gray-200)' }} />
                ) : gw.gateway === 'paypal' ? (
                  <img src="/images/paypal-logo.png" alt="PayPal" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', border: '1px solid var(--gray-200)' }} />
                ) : (
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: gw.is_enabled ? 'var(--primary, #2563eb)' : 'var(--gray-100)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: gw.is_enabled ? '#fff' : 'var(--gray-400)',
                  }}>
                    <FiCreditCard size={20} />
                  </div>
                )}
                <div>
                  <h3 style={{ margin: 0, fontSize: 16 }}>
                    {lang === 'ar' ? gw.name_ar : gw.name}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                    {gw.managed && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#eef2ff', color: '#4338ca' }}>
                        {t('No keys needed', 'بدون مفاتيح')}
                      </span>
                    )}
                    {gw.managed && gw.supplier_status === 'active' && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#dcfce7', color: '#16a34a' }}>
                        <FiCheck size={10} style={{ marginInlineEnd: 3 }} />
                        {t('Registered', 'مُسجّل')}
                        {gw.supplier_code ? ` #${gw.supplier_code}` : ''}
                      </span>
                    )}
                    {gw.managed && gw.supplier_status === 'failed' && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: '#fee2e2', color: '#b91c1c' }}>
                        {t('Registration failed', 'فشل التسجيل')}
                      </span>
                    )}
                    {!gw.managed && gw.has_test_credentials && (
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 50,
                        background: '#dcfce7', color: '#16a34a',
                      }}>
                        <FiCheck size={10} style={{ marginInlineEnd: 3 }} />
                        {t('Test keys', 'مفاتيح تجريبية')}
                      </span>
                    )}
                    {!gw.managed && gw.has_live_credentials && (
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 50,
                        background: '#dcfce7', color: '#16a34a',
                      }}>
                        <FiCheck size={10} style={{ marginInlineEnd: 3 }} />
                        {t('Live keys', 'مفاتيح حقيقية')}
                      </span>
                    )}
                    {!gw.managed && (gw.has_test_credentials || gw.has_live_credentials) && (
                      <span
                        onClick={() => handleModeToggle(gw)}
                        style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 50, cursor: 'pointer',
                          background: gw.test_mode ? '#fef3c7' : '#dbeafe',
                          color: gw.test_mode ? '#92400e' : '#1e40af',
                        }}
                        title={t('Click to switch mode', 'اضغط للتبديل')}
                      >
                        {gw.test_mode ? t('Test mode', 'وضع تجريبي') : t('Live mode', 'وضع حقيقي')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {editing !== gw.gateway && !gw.managed && (
                  <button className="btn btn-sm btn-white" onClick={() => openEdit(gw)}>
                    {t('Configure', 'إعداد')}
                  </button>
                )}
                <div
                  onClick={() => handleToggle(gw)}
                  style={{
                    width: 44, height: 24, borderRadius: 12,
                    background: gw.is_enabled ? 'var(--primary, #2563eb)' : 'var(--gray-300)',
                    position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 2,
                    left: gw.is_enabled ? 22 : 2,
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
              </div>
            </div>

            {/* Managed gateway: nothing to configure, but say what it does. */}
            {gw.managed && (
              <div style={{ borderTop: '1px solid var(--gray-100)', marginTop: 16, paddingTop: 14, fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.7 }}>
                <p style={{ margin: 0 }}>
                  {t(
                    'Card payments taken through Hadaq settle to your own MyFatoorah supplier account. This is the only online payment method shown when a customer opens your store inside the Hadaq app or website.',
                    'المدفوعات عبر حداق تُودع في حسابك كمورّد لدى ماي فاتورة. وهي طريقة الدفع الإلكتروني الوحيدة التي تظهر عند فتح متجرك من تطبيق أو موقع حداق.'
                  )}
                </p>
                {gw.supplier_status === 'failed' && gw.supplier_error && (
                  <p style={{ margin: '8px 0 0', color: '#b91c1c' }}>{gw.supplier_error}</p>
                )}
                {gw.is_enabled && gw.supplier_status === 'active' && (
                  <p style={{ margin: '8px 0 0' }}>
                    <Link to="../myfatoorah" style={{ color: 'var(--primary, #2563eb)', fontWeight: 600 }}>
                      {t('View your payments and deposits', 'عرض مدفوعاتك وإيداعاتك')} →
                    </Link>
                  </p>
                )}
              </div>
            )}

            {/* Edit form */}
            {editing === gw.gateway && (
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
                {/* Mode selector for active mode */}
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{t('Active Mode:', 'الوضع النشط:')}</span>
                  <button
                    onClick={() => setTestMode(true)}
                    style={{
                      padding: '4px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      border: '1px solid',
                      background: testMode ? '#fef3c7' : 'white',
                      borderColor: testMode ? '#f59e0b' : 'var(--gray-200)',
                      color: testMode ? '#92400e' : 'var(--gray-500)',
                    }}
                  >
                    {t('Test', 'تجريبي')}
                  </button>
                  <button
                    onClick={() => setTestMode(false)}
                    style={{
                      padding: '4px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      border: '1px solid',
                      background: !testMode ? '#dbeafe' : 'white',
                      borderColor: !testMode ? '#3b82f6' : 'var(--gray-200)',
                      color: !testMode ? '#1e40af' : 'var(--gray-500)',
                    }}
                  >
                    {t('Live', 'حقيقي')}
                  </button>
                </div>

                {/* Credential tabs */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--gray-100)' }}>
                  <button
                    onClick={() => setCredTab('test')}
                    style={{
                      padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      background: 'none', border: 'none',
                      color: credTab === 'test' ? '#92400e' : 'var(--gray-400)',
                      borderBottom: credTab === 'test' ? '2px solid #f59e0b' : '2px solid transparent',
                      marginBottom: -2,
                    }}
                  >
                    {t('Test Credentials', 'مفاتيح تجريبية')}
                    {gw.has_test_credentials && <FiCheck size={12} style={{ marginInlineStart: 6, color: '#16a34a' }} />}
                  </button>
                  <button
                    onClick={() => setCredTab('live')}
                    style={{
                      padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      background: 'none', border: 'none',
                      color: credTab === 'live' ? '#1e40af' : 'var(--gray-400)',
                      borderBottom: credTab === 'live' ? '2px solid #3b82f6' : '2px solid transparent',
                      marginBottom: -2,
                    }}
                  >
                    {t('Live Credentials', 'مفاتيح حقيقية')}
                    {gw.has_live_credentials && <FiCheck size={12} style={{ marginInlineStart: 6, color: '#16a34a' }} />}
                  </button>
                </div>

                {/* Credential fields */}
                {credTab === 'test' && renderCredFields(gw.fields, testForm, setTestForm, gw.has_test_credentials, 'test_')}
                {credTab === 'live' && renderCredFields(gw.fields, liveForm, setLiveForm, gw.has_live_credentials, 'live_')}

                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => handleSave(gw.gateway)} disabled={saving}>
                    {saving ? dt('saving', lang) : dt('save_changes', lang)}
                  </button>
                  <button className="btn btn-white" onClick={() => setEditing(null)}>
                    {dt('cancel', lang)}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {loading && gateways.length === 0 && (
        <div className="empty-state">
          <p>{dt('loading', lang)}...</p>
        </div>
      )}
    </div>
  );
}
